import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Phone, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF } from "@/lib/contact";

const navLinks = [
  { label: "Inicio", href: "#inicio" },
  { label: "Servicios", href: "#servicios" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll(); // Check initial position
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "navbar-blur bg-[#1a1a1a]/95 shadow-lg shadow-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <a
            href="#inicio"
            onClick={(e) => handleNavClick(e, "#inicio")}
            data-testid="navbar-logo"
            className="flex items-center group"
          >
            <img
              src="/escudo.png"
              alt="Escudo A.F. Puertos"
              className="h-12 md:h-16 w-auto object-contain transition-opacity duration-300 group-hover:opacity-90"
            />
            <img
              src="/logo-horizontal.png"
              alt="A.F. Puertos - Asesoría Fiscal"
              className="h-10 md:h-14 w-auto object-contain transition-opacity duration-300 group-hover:opacity-90"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-[#f8f6f3]/70 text-xs uppercase tracking-[0.2em] font-medium hover:text-[#c9a96e] transition-colors duration-300"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
            <a href={PHONE_HREF} data-testid="nav-phone-btn">
              <Button
                size="sm"
                className="bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm text-xs uppercase tracking-widest font-medium h-9 px-5"
              >
                <Phone className="w-3.5 h-3.5 mr-1.5" />
                Llamar
              </Button>
            </a>
            <a
              href="https://www.instagram.com/a.f.puertos?igsh=djVwbGVvZjhmdnVo"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram-btn"
            >
              <Button variant="outline" size="sm" className="border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e]/10 hover:text-[#c9a96e] rounded-sm h-9 px-3">
                <Instagram className="w-4 h-4" />
              </Button>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#f8f6f3] p-2 transition-transform duration-300"
            style={{ transform: mobileOpen ? "rotate(90deg)" : "rotate(0deg)" }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out bg-[#1a1a1a]/98 navbar-blur border-t border-[#c9a96e]/20 ${
          mobileOpen ? "max-h-96" : "max-h-0 border-t-0"
        }`}
      >
        <div className="px-6 py-6 space-y-4">
          {navLinks.map((link, idx) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              className={`block text-[#f8f6f3]/80 text-sm uppercase tracking-[0.15em] font-medium hover:text-[#c9a96e] transition-all duration-300 ${
                mobileOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
              }`}
              style={{ transitionDelay: mobileOpen ? `${idx * 60}ms` : "0ms" }}
            >
              {link.label}
            </a>
          ))}
          <div className="flex gap-3">
            <a href={PHONE_HREF} className="flex-1" data-testid="mobile-phone-btn">
              <Button className="w-full bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm text-xs uppercase tracking-widest font-medium">
                <Phone className="w-3.5 h-3.5 mr-1.5" />
                Llamar ahora
              </Button>
            </a>
            <a
              href="https://www.instagram.com/a.f.puertos?igsh=djVwbGVvZjhmdnVo"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-instagram-btn"
            >
              <Button variant="outline" className="border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e]/10 hover:text-[#c9a96e] rounded-sm h-9 px-3">
                <Instagram className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
